import React, { useState, useEffect } from 'react';
import { messagingService } from '../../../services/messagingService';
import { X, Users, Search } from 'lucide-react';

const NewGroupChatModal = ({ onClose, onCreateGroup }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [groupTitle, setGroupTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await messagingService?.getTeams();
      setTeams(data);
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Failed to load teams. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e?.preventDefault();
    if (!selectedTeam || !groupTitle?.trim() || creating) return;

    setCreating(true);
    try {
      await onCreateGroup(selectedTeam?.id, groupTitle?.trim());
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Failed to create group chat. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Filter teams based on search
  const filteredTeams = teams?.filter(team =>
    team?.teamName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    team?.teamNumber?.toString()?.includes(searchQuery)
  );

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create Team Group Chat</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleCreateGroup} className="space-y-4">
          {/* Group Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Chat Title
            </label>
            <input
              type="text"
              value={groupTitle}
              onChange={(e) => setGroupTitle(e?.target?.value)}
              placeholder="Enter group chat title..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
              required
            />
          </div>

          {/* Team Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Team
            </label>
            
            {/* Search Teams */}
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Teams List */}
            <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-2">
                  {[...Array(3)]?.map((_, i) => (
                    <div key={i} className="flex items-center space-x-3 animate-pulse">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredTeams?.length === 0 ? (
                <div className="p-4 text-center">
                  <Users className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    {searchQuery ? 'No teams found matching your search.' : 'No teams available.'}
                  </p>
                </div>
              ) : (
                filteredTeams?.map((team) => (
                  <div
                    key={team?.id}
                    onClick={() => setSelectedTeam(team)}
                    className={`flex items-center space-x-3 p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                      selectedTeam?.id === team?.id ? 'bg-green-50 border-green-200' : ''
                    }`}
                  >
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">
                        {team?.teamName || `Team ${team?.teamNumber}`}
                      </h4>
                      <p className="text-xs text-gray-500">
                        Team #{team?.teamNumber}
                      </p>
                    </div>
                    {selectedTeam?.id === team?.id && (
                      <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedTeam || !groupTitle?.trim() || creating}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </div>
              ) : (
                'Create Group Chat'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewGroupChatModal;